import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckSquare, Plus } from "@phosphor-icons/react";
import { Tooltip } from "@heroui/react";
import { ArrowUpRight, CheckmarkCircle, ChevronRight, Clock } from "assets/icons";
import { Task, TaskPhase } from "shared/types/task";
import { useTasks } from "shared/contexts/tasks.context";
import { useSession, useUser } from "@clerk/clerk-react";
import { calculateDaysDifference } from "shared/utils/get-dates";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import ViewTaskModal from "shared/components/viewTaskModal/viewTaskModal";
import { AddTaskModal } from "shared/components";
import { DashboardBlock, DashboardBlockHeader, DashboardBlockContent } from "../layout/DashboardBlock";
import { BlockEmptyState } from "../states/BlockStates";
import { blockStyles } from "../utils/blockStyles.utils";

export const TasksBlockNew = () => {
  const { tasks, refreshTasks } = useTasks();
  const { user } = useUser();
  const [todayTasks, setTodayTasks] = useState<Task[]>([]);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showViewTaskModal, setShowViewTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const session = useSession();

  useEffect(() => {
    setTodayTasks(
      tasks.filter(
        (task) => task.phase === TaskPhase.TODAY && task.assignee == user?.id,
      ),
    );
  }, [tasks, user?.id]);

  const handleCreateTask = (e: any) => {
    e.preventDefault();
    setShowCreateTaskModal(true);
  };

  const completeTask = async (taskId: string) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/tasks/complete`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          method: "PUT",
          body: JSON.stringify({ taskIds: [taskId] as string[] | undefined | [] }),
        },
        session.session,
      );

      if (!response.ok) {
        toast.error("Taak voltooien mislukt!");
        throw new Error("Failed to complete task.");
      } else {
        toast.success("Taak voltooid!");
        await refreshTasks();
      }

      await response.json();
    } catch (error) {
      console.error("Error completing task:", error);
    }
  };

  interface TaskCardProps {
    task: Task;
    setSelectedTask: (task: Task) => void;
    completeTask: (taskId: string) => void;
  }

  const TaskCard: React.FC<TaskCardProps> = ({
    task,
    setSelectedTask,
    completeTask,
  }) => {
    return (
      <div
        onClick={() => setSelectedTask(task)}
        className="flex w-full cursor-pointer items-center gap-2 p-3 hover:bg-moss-primary/5 transition-colors border-b border-neutral-primary/5 last:border-b-0"
      >
        <div className="flex w-full flex-col gap-1.5">
          <header className="flex w-full gap-2.5 items-start">
            <Tooltip content="Taak afvinken">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  completeTask(task.id.toString());
                }}
                className="text-moss-primary hover:text-moss-primary/80 transition-colors shrink-0 mt-0.5"
              >
                <CheckmarkCircle className="h-[18px] w-[18px]" />
              </button>
            </Tooltip>
            <p className="grow text-sm font-medium text-neutral-primary">
              {task.title}
            </p>
          </header>
          {task.deadline && (
            <div className="flex items-center gap-1.5 pl-7 text-red-600 text-xs">
              <Clock className="h-3.5 w-3.5" />
              <p>{calculateDaysDifference("deadline", task.deadline)}</p>
            </div>
          )}
        </div>
        <ChevronRight className="h-5 w-5 text-neutral-secondary-mid shrink-0" />
      </div>
    );
  };

  return (
    <DashboardBlock>
      <DashboardBlockHeader
        icon={<CheckSquare weight="duotone" size={20} />}
        title="Taken van vandaag"
        action={
          <div className="flex items-center divide-x divide-neutral-primary/20">
            <button
              onClick={handleCreateTask}
              className={`${blockStyles.link.variants.primary} pr-3 ${blockStyles.link.withIcon}`}
            >
              <Plus weight="duotone" size={18} /> Toevoegen
            </button>
            <Link
              to="/tasks"
              className={`${blockStyles.link.variants.primary} pl-3 ${blockStyles.link.withIcon}`}
            >
              Alle taken <ArrowUpRight className="h-[18px] w-[18px]" />
            </Link>
          </div>
        }
      />
      <DashboardBlockContent padding="none" className="relative overflow-hidden">
        {todayTasks.length > 0 ? (
          <>
            <div className="relative z-1 flex flex-col">
              {todayTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  completeTask={completeTask}
                  setSelectedTask={(task: any) => {
                    setSelectedTask(task);
                    setShowViewTaskModal(true);
                  }}
                />
              ))}
            </div>
            <div className="absolute bottom-0 right-0 z-0 translate-x-1/4 translate-y-1/4 text-moss-primary/8 pointer-events-none">
              <CheckSquare weight="duotone" size={120} />
            </div>
          </>
        ) : (
          <BlockEmptyState
            icon={<CheckSquare weight="duotone" size={48} />}
            title="Geen taken voor vandaag"
            description="Je bent helemaal bij!"
          />
        )}
      </DashboardBlockContent>

      <ViewTaskModal
        task={selectedTask}
        isOpen={showViewTaskModal}
        onOpenChange={(isOpen) => console.log("IS OPEN", isOpen)}
        onClose={() => setShowViewTaskModal(false)}
      />
      <AddTaskModal
        phase={TaskPhase.TODAY}
        isOpen={showCreateTaskModal}
        onOpenChange={setShowCreateTaskModal}
        onClose={() => setShowCreateTaskModal(false)}
      />
    </DashboardBlock>
  );
};

